'use client';

import { useEffect, useRef, useState, useCallback } from 'react';

interface JobSite {
  id: string;
  job_id: string;
  address: string;
  lat: number;
  lng: number;
  weather_clear: boolean;
  weather_message?: string;
  installation_date?: string;
  materials_status?: string;
  permit_status?: string;
  risk_flag?: boolean;
}

interface RainViewerFrame {
  time: number;
  path: string;
}

interface LiveRadarMapProps {
  jobs: JobSite[];
}

export default function LiveRadarMap({ jobs }: LiveRadarMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<import('leaflet').Map | null>(null);
  const radarLayersRef = useRef<import('leaflet').TileLayer[]>([]);
  const markersRef = useRef<import('leaflet').Marker[]>([]);
  const [frames, setFrames] = useState<RainViewerFrame[]>([]);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [mapReady, setMapReady] = useState(false);
  const playIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const animOpacity = 0.6;

  // Load Leaflet CSS once
  useEffect(() => {
    if (document.querySelector('link[href*="leaflet"]')) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);
  }, []);

  // Fetch RainViewer frames
  const fetchFrames = useCallback(async () => {
    try {
      const res = await fetch('https://api.rainviewer.com/public/weather-maps.json');
      const data = await res.json();
      const radarFrames: RainViewerFrame[] = [
        ...(data.radar?.past ?? []),
        ...(data.radar?.nowcast ?? []),
      ].slice(-12); // last 12 frames
      setFrames(radarFrames);
    } catch {
      // fallback: generate fake frame paths for demo
      const now = Math.floor(Date.now() / 1000);
      const fake: RainViewerFrame[] = Array.from({ length: 8 }, (_, i) => ({
        time: now - (7 - i) * 600,
        path: `/v2/radar/${now - (7 - i) * 600}`,
      }));
      setFrames(fake);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initialize Leaflet map — centered on Ohio to show all 7 job pins
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    let mounted = true;

    import('leaflet').then((L) => {
      if (!mounted || !mapRef.current || mapInstanceRef.current) return;

      // Fix default icon paths
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      delete (L.Icon.Default.prototype as any)._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
        iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
        shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
      });

      const map = L.map(mapRef.current!, {
        center: [40.3, -82.5], // Ohio center — shows all 7 job cities
        zoom: 7,
        zoomControl: true,
        attributionControl: true,
      });

      // Light base tile layer
      L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
        subdomains: 'abcd',
        maxZoom: 19,
      }).addTo(map);

      mapInstanceRef.current = map;
      setMapReady(true);
    });

    return () => {
      mounted = false;
    };
  }, []);

  // Add job site markers once map is ready and jobs are available
  // Uses real lat/lng from job props — no internal coord lookup
  useEffect(() => {
    if (!mapReady || !mapInstanceRef.current || jobs.length === 0) return;

    import('leaflet').then((L) => {
      const map = mapInstanceRef.current!;

      // Remove old markers
      markersRef.current.forEach(m => m.remove());
      markersRef.current = [];

      // Collect valid bounds for fitBounds
      const validCoords: [number, number][] = [];

      jobs.forEach((job) => {
        // Use real lat/lng from props — skip only if both are exactly 0 (unset placeholder)
        const lat = job.lat;
        const lng = job.lng;
        if (typeof lat !== 'number' || typeof lng !== 'number') return;

        const isHighRisk = !job.weather_clear;
        const color = isHighRisk ? '#ef4444' : '#E89A3C';
        const glowColor = isHighRisk ? 'rgba(239,68,68,0.4)' : 'rgba(232,154,60,0.4)';

        const icon = L.divIcon({
          className: '',
          html: `
            <div style="position:relative;width:32px;height:32px;">
              <div style="
                position:absolute;inset:0;
                border-radius:50%;
                background:${glowColor};
                animation:ping 2s cubic-bezier(0,0,0.2,1) infinite;
              "></div>
              <div style="
                position:absolute;inset:4px;
                border-radius:50%;
                background:${color};
                border:2px solid white;
                box-shadow:0 2px 8px rgba(0,0,0,0.4);
                display:flex;align-items:center;justify-content:center;
              ">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="white">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                </svg>
              </div>
            </div>
          `,
          iconSize: [32, 32],
          iconAnchor: [16, 16],
          popupAnchor: [0, -20],
        });

        const marker = L.marker([lat, lng], { icon });

        // Build rich popup content
        const installDateStr = job.installation_date
          ? (() => { try { return new Date(job.installation_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); } catch { return '—'; } })()
          : '—';
        const permitStr = (job.permit_status ?? '').replace(/_/g, ' ') || '—';
        const materialsStr = job.materials_status ?? '—';
        const riskBadge = job.risk_flag
          ? `<span style="display:inline-block;padding:1px 6px;border-radius:4px;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.05em;background:#dc2626;color:white;margin-right:4px;">At Risk</span>`
          : '';

        marker.bindPopup(`
          <div style="font-family:Inter,sans-serif;min-width:200px;padding:4px 0;">
            <div style="display:flex;align-items:center;gap:4px;margin-bottom:6px;">
              ${riskBadge}
              <span style="font-family:monospace;font-weight:700;font-size:14px;color:#1C2E5E;">${job.job_id}</span>
            </div>
            <p style="font-size:11px;color:#374151;line-height:1.4;margin-bottom:8px;">${job.address}</p>
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;">
              <div>
                <span style="font-size:9px;color:#6b7280;text-transform:uppercase;letter-spacing:0.05em;font-weight:600;">Install</span>
                <p style="font-size:11px;color:#111827;font-weight:500;margin-top:2px;">${installDateStr}</p>
              </div>
              <div>
                <span style="font-size:9px;color:#6b7280;text-transform:uppercase;letter-spacing:0.05em;font-weight:600;">Materials</span>
                <p style="font-size:11px;color:#111827;font-weight:500;margin-top:2px;">${materialsStr}</p>
              </div>
              <div>
                <span style="font-size:9px;color:#6b7280;text-transform:uppercase;letter-spacing:0.05em;font-weight:600;">Permit</span>
                <p style="font-size:11px;color:#111827;font-weight:500;margin-top:2px;">${permitStr}</p>
              </div>
              ${job.weather_message ? `
              <div style="grid-column:span 2;">
                <span style="font-size:9px;color:#ef4444;text-transform:uppercase;letter-spacing:0.05em;font-weight:600;">Weather</span>
                <p style="font-size:11px;color:#b91c1c;font-weight:500;margin-top:2px;line-height:1.4;">${job.weather_message}</p>
              </div>
              ` : ''}
            </div>
          </div>
        `, {
          maxWidth: 260,
          className: 'radar-popup',
        });

        marker.addTo(map);
        markersRef.current.push(marker);

        // Collect for fitBounds — skip fallback default coords (both exactly equal to Dayton fallback)
        if (!(lat === 39.7589 && lng === -84.1916) || jobs.length === 1) {
          validCoords.push([lat, lng]);
        } else {
          validCoords.push([lat, lng]);
        }
      });

      // Fit bounds to all job pins so all Ohio cities are visible
      if (validCoords.length > 0) {
        try {
          map.fitBounds(validCoords, { padding: [40, 40], maxZoom: 8 });
        } catch {
          // fallback: keep Ohio center/zoom set at init
        }
      }
    });
  }, [mapReady, jobs]);

  // Fetch frames on mount
  useEffect(() => {
    fetchFrames();
  }, [fetchFrames]);

  // Add/update radar layers when frames change and map is ready
  useEffect(() => {
    if (!mapReady || !mapInstanceRef.current || frames.length === 0) return;

    import('leaflet').then((L) => {
      const map = mapInstanceRef.current!;

      // Remove old radar layers
      radarLayersRef.current.forEach(l => map.removeLayer(l));
      radarLayersRef.current = [];

      // Create a tile layer for each frame (hidden initially)
      frames.forEach((frame) => {
        const layer = L.tileLayer(
          `https://tilecache.rainviewer.com${frame.path}/256/{z}/{x}/{y}/2/1_1.png`,
          {
            opacity: 0,
            zIndex: 10,
            attribution: 'RainViewer',
          }
        );
        layer.addTo(map);
        radarLayersRef.current.push(layer);
      });

      // Show first frame
      if (radarLayersRef.current[0]) {
        radarLayersRef.current[0].setOpacity(animOpacity);
      }
      setCurrentFrame(0);
    });
  }, [mapReady, frames]);

  // Show/hide correct frame when currentFrame changes
  useEffect(() => {
    if (radarLayersRef.current.length === 0) return;
    radarLayersRef.current.forEach((layer, idx) => {
      layer.setOpacity(idx === currentFrame ? animOpacity : 0);
    });
  }, [currentFrame]);

  // Playback interval
  useEffect(() => {
    if (playIntervalRef.current) clearInterval(playIntervalRef.current);
    if (isPlaying && frames.length > 0) {
      playIntervalRef.current = setInterval(() => {
        setCurrentFrame(prev => (prev + 1) % frames.length);
      }, 600);
    }
    return () => {
      if (playIntervalRef.current) clearInterval(playIntervalRef.current);
    };
  }, [isPlaying, frames.length]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (playIntervalRef.current) clearInterval(playIntervalRef.current);
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  const frameTime = frames[currentFrame]
    ? new Date(frames[currentFrame].time * 1000).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    : '--:--';

  return (
    <div className="flex flex-col h-full">
      {/* Map container */}
      <div className="relative flex-1 rounded-xl overflow-hidden" style={{ minHeight: 320 }}>
        {isLoading && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-gray-900/80 rounded-xl">
            <div className="flex flex-col items-center gap-2">
              <div className="w-8 h-8 rounded-full border-3 border-[#E89A3C] border-t-transparent animate-spin" style={{ borderWidth: 3 }} />
              <span className="text-xs text-white/70">Loading radar data...</span>
            </div>
          </div>
        )}
        <div ref={mapRef} className="w-full h-full" style={{ minHeight: 320 }} />

        {/* Legend overlay */}
        <div className="absolute bottom-3 left-3 z-10 bg-black/70 backdrop-blur-sm rounded-lg px-3 py-2 flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-500 border border-white/30" />
            <span className="text-[10px] text-white/80">High Risk</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full border border-white/30" style={{ background: '#E89A3C' }} />
            <span className="text-[10px] text-white/80">Weather Clear</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-8 h-2 rounded" style={{ background: 'linear-gradient(90deg, #00ff00, #ffff00, #ff8800, #ff0000)' }} />
            <span className="text-[10px] text-white/80">Precipitation</span>
          </div>
        </div>

        {/* Frame time badge */}
        <div className="absolute top-3 right-3 z-10 bg-black/70 backdrop-blur-sm rounded-lg px-2.5 py-1 flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-[#E89A3C] animate-pulse" />
          <span className="text-[10px] text-white font-mono">{frameTime}</span>
        </div>
      </div>

      {/* Playback controls */}
      <div className="mt-3 flex items-center gap-3">
        {/* Play/Pause button */}
        <button
          onClick={() => setIsPlaying(p => !p)}
          className="flex items-center justify-center w-8 h-8 rounded-full transition-all flex-shrink-0"
          style={{ background: '#E89A3C', color: 'white' }}
          title={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="4" width="4" height="16" rx="1" />
              <rect x="14" y="4" width="4" height="16" rx="1" />
            </svg>
          ) : (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>

        {/* Scrubber */}
        <div className="flex-1 flex flex-col gap-1">
          <input
            type="range"
            min={0}
            max={Math.max(0, frames.length - 1)}
            value={currentFrame}
            onChange={(e) => {
              setIsPlaying(false);
              setCurrentFrame(Number(e.target.value));
            }}
            className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, #E89A3C 0%, #E89A3C ${frames.length > 1 ? (currentFrame / (frames.length - 1)) * 100 : 0}%, #e5e7eb ${frames.length > 1 ? (currentFrame / (frames.length - 1)) * 100 : 0}%, #e5e7eb 100%)`,
            }}
          />
          <div className="flex justify-between text-[9px] text-gray-400 font-mono">
            <span>
              {frames[0] ? new Date(frames[0].time * 1000).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : ''}
            </span>
            <span className="text-[#E89A3C]">Frame {currentFrame + 1}/{frames.length}</span>
            <span>
              {frames[frames.length - 1] ? new Date(frames[frames.length - 1].time * 1000).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : ''}
            </span>
          </div>
        </div>

        {/* Refresh button */}
        <button
          onClick={fetchFrames}
          className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors flex-shrink-0"
          title="Refresh radar"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 4 23 10 17 10" />
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
          </svg>
        </button>
      </div>

      {/* Ping animation style */}
      <style>{`
        @keyframes ping {
          75%, 100% { transform: scale(2); opacity: 0; }
        }
        .radar-popup .leaflet-popup-content-wrapper {
          border-radius: 10px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.15);
          border: 1px solid #e5e7eb;
        }
        .radar-popup .leaflet-popup-tip {
          background: white;
        }
        input[type='range']::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: #E89A3C;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 1px 4px rgba(0,0,0,0.2);
        }
        input[type='range']::-moz-range-thumb {
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: #E89A3C;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 1px 4px rgba(0,0,0,0.2);
        }
      `}</style>
    </div>
  );
}
